<script setup lang="ts">
/**
 * Detail body — renders identically inside the drawer chrome OR on a
 * page wrapped in a `<Box padding="4">`. Props in, nothing else.
 */
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'
import { formatDateTime, formatKey, formatMoney } from '@shared/utils'
import { WITHDRAW_REQUEST_STATUS, type WithdrawRequestDetail } from '../types/withdrawRequestsTypes'

const props = defineProps<{
  details: WithdrawRequestDetail
}>()

const investorFullName = computed(() => {
  const first = props.details.investor.firstName ?? ''
  const last = props.details.investor.lastName ?? ''
  return `${first} ${last}`.trim() || '—'
})

const amountLabel = computed(() => formatMoney(props.details.currencyCode, props.details.amount))

const showTreasurySection = computed(
  () =>
    !!props.details.treasuryBankAccount &&
    props.details.status !== WITHDRAW_REQUEST_STATUS.PENDING_APPROVAL &&
    props.details.status !== WITHDRAW_REQUEST_STATUS.REJECTED &&
    props.details.status !== WITHDRAW_REQUEST_STATUS.CANCELED,
)

const showAuditSection = computed(
  () =>
    !!props.details.settlement.settlementReference ||
    !!props.details.settlement.settledAt ||
    !!props.details.audit.approvedBy ||
    !!props.details.audit.approvedAt ||
    !!props.details.rejectionReason,
)
</script>

<template>
  <Box flex direction="col" gap="4">
    <Box flex direction="col" gap="3">
      <Box flex direction="col" gap="1">
        <Text type="caption" color="muted">Amount requested</Text>
        <Text type="display" weight="bold" style="font-variant-numeric: tabular-nums">
          {{ amountLabel }}
        </Text>
      </Box>

      <Box flex justify="between">
        <Text type="body-sm" color="muted">Requested</Text>
        <Text type="body-sm" weight="medium">{{ formatDateTime(details.createdAt) }}</Text>
      </Box>

      <Box v-if="details.externalReferenceId" flex justify="between" align="center" gap="2">
        <Text type="body-sm" color="muted">Reference</Text>
        <Box flex align="center" gap="2">
          <Text type="body-sm" weight="medium">{{ details.externalReferenceId }}</Text>
          <CopyButton :value="details.externalReferenceId" label="Copy" copied-label="Copied" />
        </Box>
      </Box>
    </Box>

    <Box class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Investor</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Name</Text>
          <Text type="body-sm" weight="medium">{{ investorFullName }}</Text>
        </Box>
        <Box v-if="details.investor.email" flex justify="between">
          <Text type="body-sm" color="muted">Email</Text>
          <Text type="body-sm" weight="medium">{{ details.investor.email }}</Text>
        </Box>
        <Box v-if="details.investor.kycStatus" flex justify="between">
          <Text type="body-sm" color="muted">KYC</Text>
          <StatusBadge
            :color="details.investor.kycStatus === 'approved' ? 'success' : 'warning'"
            :label="formatKey(details.investor.kycStatus)"
          />
        </Box>
      </Box>
    </Box>

    <Box class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Destination bank account</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box v-if="details.investorBankAccount.bankName" flex justify="between">
          <Text type="body-sm" color="muted">Bank</Text>
          <Text type="body-sm" weight="medium">{{ details.investorBankAccount.bankName }}</Text>
        </Box>
        <Box v-if="details.investorBankAccount.accountHolderName" flex justify="between">
          <Text type="body-sm" color="muted">Account holder</Text>
          <Text type="body-sm" weight="medium">
            {{ details.investorBankAccount.accountHolderName }}
          </Text>
        </Box>
        <Box v-if="details.investorBankAccount.accountNumberMasked" flex justify="between">
          <Text type="body-sm" color="muted">Account number</Text>
          <Text type="body-sm" weight="medium">
            {{ details.investorBankAccount.accountNumberMasked }}
          </Text>
        </Box>
        <Box v-if="details.investorBankAccount.iban" flex justify="between">
          <Text type="body-sm" color="muted">IBAN</Text>
          <Text type="body-sm" weight="medium">{{ details.investorBankAccount.iban }}</Text>
        </Box>
      </Box>
    </Box>

    <Box v-if="showTreasurySection && details.treasuryBankAccount" class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Treasury account</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Account name</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.accountName }}</Text>
        </Box>
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Bank</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.bankName }}</Text>
        </Box>
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Currency</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.currencyCode }}</Text>
        </Box>
        <Box v-if="details.treasuryBankAccount.iban" flex justify="between">
          <Text type="body-sm" color="muted">IBAN</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.iban }}</Text>
        </Box>
      </Box>
    </Box>

    <Box v-if="showAuditSection" class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Settlement &amp; audit</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box v-if="details.audit.approvedBy" flex justify="between">
          <Text type="body-sm" color="muted">Approved by</Text>
          <Text type="body-sm" weight="medium">{{ details.audit.approvedBy }}</Text>
        </Box>
        <Box v-if="details.audit.approvedAt" flex justify="between">
          <Text type="body-sm" color="muted">Approved at</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(details.audit.approvedAt) }}</Text>
        </Box>
        <Box v-if="details.settlement.settlementReference" flex justify="between">
          <Text type="body-sm" color="muted">Settlement reference</Text>
          <Text type="body-sm" weight="medium">{{ details.settlement.settlementReference }}</Text>
        </Box>
        <Box v-if="details.settlement.settledAt" flex justify="between">
          <Text type="body-sm" color="muted">Settled at</Text>
          <Text type="body-sm" weight="medium">{{
            formatDateTime(details.settlement.settledAt)
          }}</Text>
        </Box>
        <Box v-if="details.rejectionReason" flex justify="between" gap="3">
          <Text type="body-sm" color="muted">Rejection reason</Text>
          <Text type="body-sm" weight="medium" align="right">{{ details.rejectionReason }}</Text>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.section {
  border-top: 1px solid var(--p-content-border-color);
  padding-top: 1rem;
}
.section-label {
  display: block;
  margin-bottom: 0.5rem;
}
.rows {
  padding-top: 0.25rem;
}
</style>
